import React, { useState, useEffect, useRef } from "react";
import { useCart } from "../context/CartContext";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import axios from "../api/axios";
import "./styles/Checkout.css"; // ✅ Import styles
import { slugify } from "../utility/slugify";
import SEO from "../seo/SEO";
import config from "../config/config";
import generateBreadcrumbs from "../utility/generateBreadcrumbs";
import { useStore } from "../context/StoreContext";


import { useAnalytics } from "../context/AnalyticsProvider";


const Checkout = () => {
  const { cart } = useCart();
  const { auth } = useAuth();
  const { products } = useStore();


  const hasFiredBeginCheckoutRef = useRef(false);


  const analytics = useAnalytics();

  const [buttonLoading, setButtonLoading] = useState(false);
  const [fees, setFees] = useState({
    subtotal: 0,
    shipping: 0,
    stripeFee: 0,
    baseInsurance: 0, // ✅ Ensure this exists
    calculatedInsurance: 0, // ✅ Ensure this exists
    totalWithoutInsurance: 0, // ✅ Store the price without insurance
    totalWithInsurance: 0, // ✅ Store the price with insurance
    total: 0, // ✅ Ensure this exists
  });

  const [ageConfirmed, setAgeConfirmed] = useState(false);

  

  
  
  const [addInsurance, setAddInsurance] = useState(false);
  
  useEffect(() => {
    const fetchFees = async () => {
      try {
        const response = await axios.post("/api/checkout/calculate-fees", { cart });
  
        setFees({
          ...response.data,
          totalWithoutInsurance: response.data.totalWithoutInsurance,
          totalWithInsurance: response.data.totalWithInsurance,
        });
  
      } catch (error) {
        console.error("Error fetching fees:", error);
      }
    };
  
    fetchFees();
  }, [cart]); // ✅ Only fetch when cart changes (not on insurance toggle)
  

  // 🟦 GA4: Fire begin_checkout exactly ONCE per checkout page load
  useEffect(() => {
    if (cart.length === 0) return;
    if (hasFiredBeginCheckoutRef.current) return; // 👈 prevents StrictMode double-fire

    analytics.track("begin_checkout", {
      currency: "USD",
      value: Number(fees.subtotal).toFixed(2),
      items: cart.map(item => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity,
        category: item.category_name,
        stock_remaining: item.stock - item.quantity,
      }))
    });

    hasFiredBeginCheckoutRef.current = true;
  }, [cart.length]);  // 👈 only depends on "cart present"


  
  

  const [form, setForm] = useState({
    name: auth.user ? auth.user.name : "",
    email: auth.user ? auth.user.email : "",
    address: "",
    city: "",
    state: "",
    zip: "",
  });

  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const stateAgeRestrictions = {
    "AL": 18, "AK": 21, "AZ": 18, "AR": 18, "CA": 18, "CO": 18, "CT": 18, "DE": 18, 
    "FL": 18, "GA": 18, "HI": 18, "ID": 18, "IL": 18, "IN": 18, "IA": 18, "KS": 18, 
    "KY": 21, "LA": 18, "ME": 18, "MD": 18, "MA": 18, "MI": 18, "MN": 18, "MS": 18, 
    "MO": 18, "MT": 18, "NE": 18, "NV": 18, "NH": 18, "NJ": 18, "NM": 18, "NY": 18, 
    "NC": 18, "ND": 18, "OH": 18, "OK": 18, "OR": 18, "PA": 18, "RI": 18, "SC": 18, 
    "SD": 18, "TN": 18, "TX": 18, "UT": 18, "VT": 18, "VA": 18, "WA": 18, "WV": 18, 
    "WI": 18, "WY": 21, // ✅ Defaulting all to 18 (Modify per state laws)
  };
  
  const requiredAge = stateAgeRestrictions[form.state] || 18; // Default to 18
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setButtonLoading(true);



      // ✅ Fire Google-recognized add_shipping_info event
      analytics.track("add_shipping_info", {
        currency: "USD",
        value: fees.subtotal.toFixed(2), // total at this stage
        shipping_tier: "standard", // or "express", "insured", etc. if you have tiers
        shipping_insurance_selected: addInsurance,
        items: cart.map(item => ({
          item_id: item.id,
          item_name: item.name,
          price: item.price,
          quantity: item.quantity,
          category: item.category_name,
          stock_remaining: item.stock - item.quantity,
        })),
      });


      analytics.track("add_payment_info", {
        currency: "USD",
        value: fees.subtotal.toFixed(2),
        payment_type: "stripe_checkout",
        items: cart.map(item => ({
          item_id: item.id,
          item_name: item.name,
          price: item.price,
          quantity: item.quantity,
          category: item.category_name,
          stock_remaining: item.stock - item.quantity,
        })),
      });

    let csrfToken = null;
    if (auth.isAuthenticated) {
        csrfToken = document.cookie
            .split("; ")
            .find((row) => row.startsWith("csrfToken="))
            ?.split("=")[1];

        if (!csrfToken) {
            setErrorMessage("Missing CSRF token. Please refresh the page and try again.");
            setButtonLoading(false);
            return;
        }
    }
    const ageStatement = `I confirm that I am at least ${requiredAge} years old and legally allowed to purchase this item. By checking this box, I acknowledge that this serves as my electronic signature.`;
    try {
        const payload = {
            cart,
            totalPrice: fees.total, // ✅ Send the correct total including fees
            addInsurance, // ✅ Send if the user opted for insurance
            userId: auth.isAuthenticated ? auth.user.id : null,
            ...form,
            ageConfirmation: ageStatement
        };

        const config = csrfToken ? { headers: { "x-csrf-token": csrfToken } } : {};

        const response = await axios.post("/api/checkout", payload, config);

        window.location.href = response.data.url;

    } catch (error) {
        setErrorMessage(error.response?.data?.error || "An unexpected error occurred during checkout.");
        setButtonLoading(false);
    }
};


const nameCounts = products.reduce((acc, p) => {
  acc[p.name] = (acc[p.name] || 0) + 1;
  return acc;
}, {});

const duplicateNames = new Set(
  Object.entries(nameCounts)
    .filter(([_, count]) => count > 1)
    .map(([name]) => name)
);


  return (
    <>
        <SEO
            robots="noindex, nofollow"
            title="Secure Checkout - Withrow Knives"
            description="Complete your purchase securely at Withrow Knives. Our checkout process is fast, easy, and protected by Stripe for safe payments."
            keywords={[
                "secure checkout",
                "buy custom knives",
                "purchase handmade knives",
                "Withrow Knives checkout",
                "Stripe payments"
            ]}
            url={`${config.metadata.siteUrl}/secure-checkout`}
            jsonLd={{
                "@context": "https://schema.org",
                "@type": "CheckoutPage",
                "name": "Secure Checkout - Withrow Knives",
                "url": `${config.metadata.siteUrl}/secure-checkout`,
                "description": "Complete your purchase securely with Stripe. Withrow Knives offers fast and safe checkout for custom handmade knives.",
                "potentialAction": {
                    "@type": "BuyAction",
                    "target": `${config.metadata.siteUrl}/secure-checkout`,
                    "name": "Proceed to Payment"
                },
                "breadcrumb": generateBreadcrumbs(["shopping-cart", "secure-checkout"]),
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": `${config.metadata.siteUrl}/secure-checkout`
                }
            }}
        />


    
      <div className={`checkout-container ${buttonLoading ? "loading" : ""}`}>
        <h2 className="checkout-title">Checkout</h2>

        {errorMessage && <p className="checkout-error">{errorMessage}</p>}

        {/* ✅ Order Summary Section */}
        <div className="order-summary">
          <Link to='/shopping-cart' className="back-to-cart">
            <i className="fa-solid fa-arrow-left"></i> Back to cart
          </Link>
          <h3>Order Summary</h3>
          <div className="order-items">
            {cart.map((item) => (
              <div key={item.id} className="order-item">
                <div className="order-item-details">
                  <Link
                    to={`/shop-knives/knife/${item.id}/${slugify(item.name)}`}
                    className="order-item-name"
                  >
                      {item.name}
                      {duplicateNames.has(item.name) && (
                        <> <span className="order-item-id">(#{item.id})</span></>
                      )}
                  </Link>
                  <p className="order-item-quantity">
                    Quantity: <span>{item.quantity}</span>
                  </p>
                </div>
                <div className="order-item-pricing">
                  <p>${Number(item.price).toFixed(2)} each</p>
                  <p>
                    {item.quantity} x {Number(item.price).toFixed(2)} = 
                    <span> ${ (Number(item.price) * item.quantity).toFixed(2) }</span>
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="checkout-details-wrapper">

            <div className="pickup-note">
              <h4> <i className="fa-solid fa-circle-question"></i> Local Pickup </h4>
              <p>If you would like to pickup your knife locally or discuss meeting up (typically anywhere in the Charleston-Huntington area), please <Link to="/contact">contact us</Link> directly instead of ordering online. This will save you the shipping and processing fees.</p>
            </div>
          
            <div className="checkout-breakdown">
              <p><i>Subtotal:</i> <strong>${fees.subtotal.toFixed(2)}</strong></p>
              <p><i>Stripe Processing Fee:</i> <strong>${fees.stripeFee.toFixed(2)}</strong></p> {/* ✅ Always the same */}

              
              <p><i>Shipping & Handling:</i> <strong>${fees.shipping.toFixed(2)}</strong></p>
              {addInsurance && (
                <p><i>Shipping Insurance:</i> <strong>${fees.calculatedInsurance.toFixed(2)}</strong></p>
              )}

              <hr/>
              <label className="insurance-toggle">
                <input
                  type="checkbox"
                  checked={addInsurance}
                  onChange={() => setAddInsurance((prev) => !prev)}
                />
                <span>
                  Add Shipping Insurance (+${fees.calculatedInsurance.toFixed(2)}) {/* ✅ Uses correct insurance cost */}
                </span>



              </label>
              <h3>Total: 
                <span>${(addInsurance ? fees.totalWithInsurance : fees.totalWithoutInsurance).toFixed(2)}</span>
              </h3>







            </div>
          </div>
          <Link to='/shopping-cart' className="back-to-cart">
            <i className="fa-solid fa-arrow-left"></i> Back to cart
          </Link>
        </div>

        <h3>Shipping Address</h3>
        <p>This is the address your items will be shipped to. Please double check everything before submitting.</p>
        {/* ✅ Checkout Form */}
        <form className="checkout-form" onSubmit={handleSubmit}>
          <label htmlFor="checkout-name" className="sr-only">Full Name</label>
          <input 
            id="checkout-name"
            type="text" 
            name="name" 
            placeholder="Full Name" 
            value={form.name} 
            onChange={handleChange} 
            required 
          />

          <label htmlFor="checkout-email" className="sr-only">Email</label>
          <input 
            id="checkout-email"
            type="email" 
            name="email" 
            placeholder="Email" 
            value={form.email} 
            onChange={handleChange} 
            required 
          />

          <label htmlFor="checkout-address" className="sr-only">Address</label>
          <input 
            id="checkout-address"
            type="text" 
            name="address" 
            placeholder="Address" 
            value={form.address} 
            onChange={handleChange} 
            required 
          />

          {/* ✅ City, State, Zip Row */}
          <div className="form-row">
            <label htmlFor="checkout-city" className="sr-only">City</label>
            <input 
              id="checkout-city"
              type="text" 
              name="city" 
              placeholder="City" 
              value={form.city} 
              onChange={handleChange} 
              required 
            />

            {/* ✅ State Dropdown */}
            <label htmlFor="checkout-state" className="sr-only">State</label>
            <select 
              id="checkout-state"
              name="state" 
              value={form.state} 
              onChange={handleChange} 
              required
            >
              <option value="" disabled>Select State</option>
              {[
                "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", 
                "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", 
                "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", 
                "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", 
                "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
              ].map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>

            {/* ✅ Zip Code Restricted to 5 Digits */}
            <label htmlFor="checkout-zip" className="sr-only">Zip Code</label>
            <input 
              id="checkout-zip"
              type="text" 
              name="zip" 
              placeholder="Zip Code" 
              value={form.zip} 
              onChange={(e) => {
                const zip = e.target.value.replace(/\D/g, "").slice(0, 5);
                handleChange({ target: { name: "zip", value: zip } });
              }} 
              required 
              inputMode="numeric"
              pattern="\d{5}"
              title="Enter a 5-digit zip code"
            />
          </div>

          <label className="age-confirmation">
            <input
              type="checkbox"
              checked={ageConfirmed}
              onChange={(e) => setAgeConfirmed(e.target.checked)}
              required
            />
            <span>
              By checking this box, you are stating:  
              <strong> "I affirm that I am at least {requiredAge} years old and legally allowed to purchase this item.  
I certify that the name and shipping information I have provided are accurate and truthful.  
I understand that this checkbox serves as my electronic signature and a legally binding statement of my age and eligibility to purchase this item."</strong>
            </span>
          </label>


          {/* ✅ Checkout Button */}
          <button type="submit" className="checkout-btn" disabled={buttonLoading || !ageConfirmed}>
            {buttonLoading ? <div className="spinner" style={{margin: '0.2rem 5rem'}}></div> : "Proceed to Payment"}
          </button>


          {/* ✅ Stripe Notice */}
          <p className="secure-payment-note">
            Payments are securely processed with <strong>Stripe</strong>. 
            We never see or store your payment information.
          </p>
        </form>
      </div>

    </>
  );
};

export default Checkout;
